import React from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageContainer from '@/components/PageContainer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAuthStore } from '@/stores/authStore';
import { changePassword } from '@/api/admin';

interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [form] = Form.useForm<ChangePasswordValues>();

  const mutation = useMutation({
    mutationFn: (values: { currentPassword: string; newPassword: string }) =>
      changePassword(values.currentPassword, values.newPassword),
    onSuccess: () => {
      message.success('密码修改成功，请重新登录');
      useAuthStore.getState().setUser(null);
      navigate('/login', { replace: true });
    },
    onError: (error: unknown) => {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || '密码修改失败';
      message.error(msg);
    },
  });

  const handleFinish = (values: ChangePasswordValues) => {
    mutation.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  };

  return (
    <PageContainer title="修改密码">
      <Card style={{ maxWidth: isMobile ? '100%' : 400, margin: '0 auto' }}>
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="当前密码" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 4, message: '密码至少4个字符' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_: unknown, value: string) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="确认新密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={mutation.isPending} block>
              修改密码
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </PageContainer>
  );
};

export default ChangePasswordPage;
